import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTopicsStore, useCardsStore } from '@/stores'
import { generateBranchLid, generateContinueLid, createFirstLid, formatLidForDisplay } from '@/utils/lid'
import type { CardTreeNode } from '@/types'
import './NewCardPage.css'

export default function NewCardPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { getTopicsSorted } = useTopicsStore()
  const { addCard, addInboxCard, getCardTree, getAllLids, getCardsByTopic } = useCardsStore()
  const sortedTopics = getTopicsSorted()

  // Check if we're in inbox mode
  const isInboxMode = searchParams.get('inbox') === 'true'

  // Form state - restore from URL params if switching from inbox mode
  const [topicId, setTopicId] = useState(searchParams.get('topic') || '')
  const [title, setTitle] = useState(searchParams.get('title') || '')
  const [content, setContent] = useState(searchParams.get('content') || '')
  const [parentLid, setParentLid] = useState(searchParams.get('parent') || '')

  // 簡化規則：有 parent → branch，無 parent → continue
  // 如果 URL 有指定 type，優先使用
  const urlType = searchParams.get('type')
  const branchType: 'branch' | 'continue' =
    (urlType === 'branch' || urlType === 'continue')
      ? urlType
      : (parentLid ? 'branch' : 'continue')

  // Preview L-ID
  const [previewLid, setPreviewLid] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Calculate preview L-ID when inputs change
  useEffect(() => {
    if (!topicId) {
      setPreviewLid(null)
      return
    }

    const allLids = getAllLids()
    const topicCards = getCardsByTopic(topicId)

    if (topicCards.length === 0) {
      setPreviewLid(createFirstLid(topicId))
      return
    }

    if (!parentLid) {
      // New root card
      const rootLids = allLids.filter((lid) => {
        const parts = lid.split('-')
        return parts[0] === topicId && /^\d+$/.test(parts[1])
      })
      const maxRoot = rootLids
        .map((lid) => parseInt(lid.split('-')[1], 10))
        .reduce((max, n) => Math.max(max, n), 0)
      setPreviewLid(`${topicId}-${maxRoot + 1}`)
      return
    }

    try {
      if (branchType === 'branch') {
        setPreviewLid(generateBranchLid(parentLid, allLids))
      } else {
        setPreviewLid(generateContinueLid(parentLid, allLids))
      }
    } catch {
      setPreviewLid(null)
    }
  }, [topicId, parentLid, urlType, getAllLids, getCardsByTopic])

  // Get card tree for selected topic
  const cardTree = topicId ? getCardTree(topicId) : []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!title) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      if (isInboxMode) {
        // Save to inbox
        await addInboxCard({ title, content })
        navigate('/inbox')
      } else {
        // Save to topic with L-ID
        if (!topicId || !previewLid) return

        const card = await addCard({
          topicId,
          title,
          content,
          parentLid: parentLid || null,
          branchType: parentLid ? branchType : undefined,
        })
        navigate(`/card/${encodeURIComponent(card.lid)}`)
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create card')
      setIsSubmitting(false)
    }
  }

  const handleParentSelect = (lid: string) => {
    setParentLid(lid)
  }

  // Inbox mode: simplified form
  if (isInboxMode) {
    return (
      <div className="new-card-page">
        <div className="page-header">
          <div>
            <h1>Quick Capture</h1>
            <p className="text-secondary">
              Save your thought now, place it in your knowledge tree later
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="inbox-capture-form card">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              className="input"
              placeholder="What's on your mind?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onInput={(e) => {
                // Workaround for IME input scroll issue on Linux
                const input = e.target as HTMLInputElement
                const pos = input.selectionStart
                if (pos !== null) {
                  requestAnimationFrame(() => {
                    input.setSelectionRange(pos, pos)
                  })
                }
              }}
              autoFocus
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content (optional)</label>
            <textarea
              id="content"
              className="input content-input"
              placeholder="Add more details if you want..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {submitError && (
            <div className="form-error">{submitError}</div>
          )}

          <div className="form-actions">
            <Link to="/inbox" className="btn btn-secondary">
              Cancel
            </Link>
            <Link
              to={`/card/new?title=${encodeURIComponent(title)}&content=${encodeURIComponent(content)}`}
              className="btn btn-secondary"
            >
              Place in Topic Instead
            </Link>
            <button type="submit" className="btn btn-primary" disabled={!title || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save to Inbox'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  // Normal mode: full form with topic/parent selection
  return (
    <div className="new-card-page">
      <div className="page-header">
        <div>
          <h1>New Card</h1>
          <p className="text-secondary">
            Choose where to place your card in the knowledge tree
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="new-card-form">
        <div className="form-layout">
          <div className="form-main">
            {/* Topic Selection */}
            <div className="form-group">
              <label htmlFor="topic">Topic</label>
              <select
                id="topic"
                className="input"
                value={topicId}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === 'inbox') {
                    // Redirect to inbox mode
                    navigate('/card/new?inbox=true')
                    return
                  }
                  setTopicId(value)
                  setParentLid('')
                }}
                required
              >
                <option value="">Select a topic...</option>
                <option value="inbox">📥 Inbox (decide later)</option>
                {sortedTopics.length > 0 && (
                  <option disabled>──────────</option>
                )}
                {sortedTopics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.id}/ - {topic.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                className="input"
                placeholder="Card title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onInput={(e) => {
                  // Workaround for IME input scroll issue on Linux
                  const input = e.target as HTMLInputElement
                  const pos = input.selectionStart
                  if (pos !== null) {
                    requestAnimationFrame(() => {
                      input.setSelectionRange(pos, pos)
                    })
                  }
                }}
                required
              />
            </div>

            {/* Content */}
            <div className="form-group">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                className="input content-input"
                placeholder="Write your note in Markdown. Use [[L-ID]] to create cross-references."
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              <small className="form-hint">
                Supports Markdown. Use [[1/1a]] syntax to link to other cards.
              </small>
            </div>
          </div>

          <div className="form-sidebar">
            {/* L-ID Preview */}
            <div className="lid-preview card">
              <h3>Generated L-ID</h3>
              {previewLid ? (
                <div className="preview-lid">{formatLidForDisplay(previewLid)}</div>
              ) : (
                <div className="preview-placeholder">Select a topic to generate L-ID</div>
              )}
            </div>

            {/* Parent Selection */}
            {topicId && (
              <div className="parent-selection card">
                <h3>Place After</h3>
                {cardTree.length === 0 ? (
                  <p className="text-secondary text-sm">
                    No cards yet. This will be the first card.
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      className={`parent-option ${!parentLid ? 'selected' : ''}`}
                      onClick={() => setParentLid('')}
                    >
                      <span className="lid-badge">New Root</span>
                      <span>Start a new sequence</span>
                    </button>
                    <div className="parent-tree">
                      <SelectableTree
                        nodes={cardTree}
                        selectedLid={parentLid}
                        onSelect={handleParentSelect}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {submitError && (
          <div className="form-error">{submitError}</div>
        )}
        <div className="form-actions">
          <Link to={topicId ? `/topic/${topicId}` : '/dashboard'} className="btn btn-secondary">
            Cancel
          </Link>
          <Link
            to={`/card/new?inbox=true&title=${encodeURIComponent(title)}&content=${encodeURIComponent(content)}`}
            className="btn btn-secondary"
          >
            Save to Inbox Instead
          </Link>
          <button type="submit" className="btn btn-primary" disabled={!previewLid || !title || isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Card'}
          </button>
        </div>
      </form>
    </div>
  )
}

// Selectable tree component for parent selection
interface SelectableTreeProps {
  nodes: CardTreeNode[]
  selectedLid: string
  onSelect: (lid: string) => void
  level?: number
}

function SelectableTree({ nodes, selectedLid, onSelect, level = 0 }: SelectableTreeProps) {
  return (
    <div className="selectable-tree">
      {nodes.map((node) => (
        <div key={node.card.lid} className="selectable-item">
          <button
            type="button"
            className={`parent-option ${selectedLid === node.card.lid ? 'selected' : ''}`}
            onClick={() => onSelect(node.card.lid)}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
          >
            <span className="lid-badge">{formatLidForDisplay(node.card.lid)}</span>
            <span className="option-title">{node.card.title}</span>
          </button>
          {node.children.length > 0 && (
            <SelectableTree
              nodes={node.children}
              selectedLid={selectedLid}
              onSelect={onSelect}
              level={level + 1}
            />
          )}
        </div>
      ))}
    </div>
  )
}
